import React, { FormEventHandler, Fragment, useState, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useForm, FormProvider } from 'react-hook-form';
import { TypeOf } from 'yup';
import { styled } from '@linaria/react';
import { useAtom } from 'jotai';
import { focusAtom } from 'jotai/optics';
import { css } from '@linaria/core';
import {
  ButtonGroup,
  Radio,
  RadioGroup,
  Text,
  Heading,
  Stack,
  usePopoverOpen,
  colors,
} from '@spaceshipapp/cambridge';
import { parcelMapAtom } from '../../../contexts/shipment';
import Button from '../../../components/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import pkgSchema from '../../../schemas/ship/pkg';
import { getPresets, getMatchedPresetSize, getPresetSize } from '../utils';
import { ParcelPresetId } from '../../../common/package';
import { ShipmentType } from '../../../types/common';
import * as analytics from '../../../analytics/quotation';
import DimensionsForm from './DimensionsForm';

enum Mode {
  Presets = 'presets',
  Custom = 'custom',
}

interface Props {
  parcelId: string;
}

const SizePicker: React.FC<Props> = ({ parcelId }) => {
  const { t } = useTranslation('common');
  const resolver = yupResolver(pkgSchema);
  const parcelAtom = useMemo(
    () => focusAtom(parcelMapAtom, (o) => o.prop(parcelId)),
    [parcelMapAtom],
  );
  const [parcel, updateParcel] = useAtom(parcelAtom);
  const { length, width, height, weight } = parcel || {};
  const [mode, setMode] = useState(
    getMatchedPresetSize(parcel, ShipmentType.PARCEL) ? Mode.Presets : Mode.Custom,
  );
  const isCustom = mode === Mode.Custom;
  const methods = useForm<TypeOf<typeof pkgSchema>>({
    resolver,
    shouldUnregister: true,
    context: { isCustom },
  });
  const { formState } = methods;
  const { isSubmitting } = formState;
  const presets = getPresets(ShipmentType.PARCEL);
  const deafultSize = getMatchedPresetSize(parcel, ShipmentType.PARCEL) || 'small';
  const [size, setSize] = useState<ParcelPresetId>(deafultSize as ParcelPresetId);
  const togglePopover = usePopoverOpen();

  const description: Record<ParcelPresetId, string> = {
    small: t('Parcel.parcelSmall'),
    medium: t('Parcel.parcelMedium'),
    large: t('Parcel.parcelLarge'),
  };

  const handleSelect: FormEventHandler = (e) => {
    setSize((e.target as HTMLFormElement).value);
  };

  const handleSubmit = (data: TypeOf<typeof pkgSchema>) => {
    const parcelSize = isCustom ? data : getPresetSize(size, ShipmentType.PARCEL);
    updateParcel(parcelSize);
    togglePopover(false);

    analytics.sendParcelSizeChanged(parcelSize);
  };

  const handleClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMode(e.target.value as Mode);
  };

  return (
    <FormProvider {...methods}>
      <Stack gap="lg" as="form" onSubmit={methods.handleSubmit(handleSubmit)}>
        <Text size="sm">{t('Parcel.sizePickerDescription')}</Text>
        <RadioGroup value={mode} onChange={handleClick}>
          <ButtonGroup size="sm">
            <Button
              as={Radio}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              type="radio"
              variant={!isCustom ? `primary` : `default`}
              className={btnStyle}
              name="mode"
              value="presets"
              data-tracking-label={t('commonEn:Parcel.modePresets')}
            >
              {t('Parcel.modePresets')}
            </Button>
            <Button
              as={Radio}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              type="radio"
              variant={isCustom ? `primary` : `default`}
              className={btnStyle}
              name="mode"
              value="custom"
              data-tracking-label={t('commonEn:Parcel.modeCustom')}
            >
              {t('Parcel.modeCustom')}
            </Button>
          </ButtonGroup>
        </RadioGroup>
        {!isCustom ? (
          <Fragment>
            <List>
              <RadioGroup value={size} onChange={handleSelect}>
                {Object.entries(presets).map(([id, { length, width, height, weight }]) => (
                  <Radio id={id} name="size" value={id} key={id} className={labelStyle}>
                    <Heading size="sm" as="h3">
                      {description[id as ParcelPresetId]}
                    </Heading>
                    <Text size="sm">
                      {t('Parcel.description', { length, width, height, weight })}
                    </Text>
                  </Radio>
                ))}
              </RadioGroup>
            </List>
          </Fragment>
        ) : (
          <DimensionsForm length={length} width={width} height={height} weight={weight} />
        )}
        <Button
          type="submit"
          block
          variant="primary"
          disabled={isSubmitting}
          data-tracking-label={t('commonEn:Parcel.buttonSubmit')}
        >
          {t('Parcel.buttonSubmit')}
        </Button>
      </Stack>
    </FormProvider>
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:first-child) {
    margin-top: 1rem;
  }
`;

const labelStyle = css`
  display: flex;
  flex-direction: row-reverse;
  padding: 1em;
  border: 1px solid ${colors.border};

  > *:last-child {
    margin-left: 0em;
    margin-right: 0.5em;
  }
`;

const btnStyle = css`
  flex-grow: 1;
  text-align: center;

  > span {
    /* hide fake radio */
    display: none;
  }
`;

export default SizePicker;
